export type PremiumCapacityConfig = {
    dashboardSeatsPerUnit: number;
    familyLicensesPerUnit: number;
    minimumUnits: number;
    maximumSelfServeUnits: number;
};

export type PremiumCapacity = {
    units: number;
    dashboardSeats: number;
    familyLicenses: number;
    exceedsSelfServeMaximum: boolean;
};

// Treat every partial unit as a whole paid unit and keep the public selector above zero.
const normalizeRequestedUnits = (value: number, minimumUnits: number): number => {
    if (!Number.isFinite(value)) return minimumUnits;
    return Math.max(minimumUnits, Math.ceil(value));
};

// Build the single capacity result consumed by both linked number controls.
export const getPremiumCapacity = (
    requestedUnits: number,
    config: PremiumCapacityConfig
): PremiumCapacity => {
    const units = normalizeRequestedUnits(requestedUnits, config.minimumUnits);
    return {
        units,
        dashboardSeats: units * config.dashboardSeatsPerUnit,
        familyLicenses: units * config.familyLicensesPerUnit,
        exceedsSelfServeMaximum: units > config.maximumSelfServeUnits,
    };
};

// Convert a requested seat count into the whole subscription units needed to supply it.
export const getPremiumCapacityForSeats = (
    requestedSeats: number,
    config: PremiumCapacityConfig
): PremiumCapacity => getPremiumCapacity(
    requestedSeats / config.dashboardSeatsPerUnit,
    config
);

// Convert a requested family count into whole units, rounding the delivered allowance up.
export const getPremiumCapacityForFamilies = (
    requestedFamilies: number,
    config: PremiumCapacityConfig
): PremiumCapacity => getPremiumCapacity(
    requestedFamilies / config.familyLicensesPerUnit,
    config
);

// Prices published in the catalogue are per unit; the browser only presents the derived total.
export const getPremiumTotalPrice = (unitPrice: number | undefined, units: number): number | undefined =>
    typeof unitPrice === 'number' && Number.isFinite(unitPrice) && unitPrice >= 0
        ? unitPrice * units
        : undefined;
